
import { useState } from "react";
import { Shell } from "@/components/layout/Shell";
import { Button } from "@/components/ui/button";
import { Edit, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCategories } from "@/hooks/useCategories";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { DeleteAllConfirmationDialog } from "@/components/common/DeleteAllConfirmationDialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function CategoriesPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();
  
  const { 
    useCategoriesQuery,
    useDeleteCategoryMutation
  } = useCategories();
  
  const { data: categories = [], isLoading, isError } = useCategoriesQuery();
  const deleteCategoryMutation = useDeleteCategoryMutation();
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  // Filter categories based on search term
  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteCategoryMutation.mutateAsync(id);
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  const handleDeleteAllCategories = async () => {
    if (categories.length === 0) return;
    
    try {
      setIsDeletingAll(true);
      
      // Delete categories one by one
      for (const category of categories) {
        await deleteCategoryMutation.mutateAsync(category.id);
      }
      
      toast.success(t('categories.allDeleted', 'All categories have been deleted'));
    } catch (error) {
      console.error("Error deleting all categories:", error);
      toast.error(t('categories.deleteAllError', 'Failed to delete all categories'));
    } finally {
      setIsDeletingAll(false);
    }
  };

  if (isLoading) {
    return (
      <Shell>
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div>
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-32 mt-2" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </Shell>
    );
  }

  if (isError) {
    return (
      <Shell>
        <div className="p-6 bg-destructive/10 rounded-md">
          <h1 className="text-xl font-medium text-destructive">Error loading categories</h1>
          <p className="mt-2">
            Could not load categories. Please try again later.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t('categories.title', 'Categories')}</h1>
            <p className="text-muted-foreground mt-2">
              {t('categories.description', 'Manage contract categories')}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative w-full sm:w-64">
              <Input
                type="search"
                placeholder={t('categories.search', 'Search categories...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant="destructive" 
                onClick={() => setDeleteAllDialogOpen(true)}
                disabled={categories.length === 0}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('categories.deleteAll', 'Delete All')}
              </Button>
              <Button asChild>
                <Link to="/categories/new">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('categories.add', 'Add Category')}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {filteredCategories.length === 0 ? (
          <div className="text-center py-12 border rounded-md">
            <h3 className="text-lg font-medium">No categories found</h3>
            <p className="text-muted-foreground mt-1">
              {searchTerm ? "Try a different search term" : "Create your first category to get started"}
            </p>
            {!searchTerm && (
              <Button className="mt-4" asChild>
                <Link to="/categories/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Category
                </Link>
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredCategories.map((category) => (
              <div
                key={category.id}
                className="rounded-lg border bg-card shadow-sm transition-all hover:shadow-md animate-fade-in"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold">{category.name}</h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="h-8 w-8 p-0"
                        >
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          onClick={() => navigate(`/categories/${category.id}/edit`)}
                          className="flex items-center"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem 
                              onSelect={(e) => e.preventDefault()}
                              className="text-destructive focus:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              <span>Delete</span>
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete category?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove the category from any contracts that use it.
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDeleteCategory(category.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.description || "No description"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete All Confirmation Dialog */}
      <DeleteAllConfirmationDialog
        open={deleteAllDialogOpen}
        onOpenChange={setDeleteAllDialogOpen}
        onConfirm={handleDeleteAllCategories}
        title={t('categories.deleteAllTitle', 'Delete all categories?')}
        description={t('categories.deleteAllDescription', 'This will permanently remove all categories. This action cannot be undone.')}
        isDeleting={isDeletingAll}
      />
    </Shell>
  );
}
